import React from 'react';
import { motion } from 'motion/react';
import { CheckCircle, X, Plus, Trash2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface Appointment {
    id: string;
    total_amount_ttc?: number;
    amount_already_paid?: number;
}

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

// Clôture d'un rendez-vous RDV Pro (Restauration) — version allégée de AdminCompletionModal.tsx (Prestation) :
// pas d'étape "extras" ici, un RDV Pro n'a pas de catalogue de prestations, son montant total est déjà
// fixé une fois pour toutes par la Démarche client qui l'a créé (total_amount_ttc, connu dès la création).
// On ne fait donc qu'encaisser le solde restant, jamais ressaisir ce qui a déjà été payé en ligne.
export function RdvCompletionModal({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: () => void; }) {
    const totalTtc = Math.max(0, Number(appt.total_amount_ttc) || 0);
    const alreadyPaidOnline = Math.max(0, Number(appt.amount_already_paid) || 0);
    const remainingAfterOnline = Math.max(0, totalTtc - alreadyPaidOnline);

    const [paymentStatus, setPaymentStatus] = React.useState<'encaisse' | 'a_encaisser'>(remainingAfterOnline > 0 ? 'a_encaisser' : 'encaisse');
    const [paymentEntries, setPaymentEntries] = React.useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    const [newPaymentAmount, setNewPaymentAmount] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    // Question posée une fois le RDV clôturé (2026-09-05, Emeni) : jamais de création automatique et
    // silencieuse d'un dossier de Suivi de prestation, toujours ce choix explicite.
    const [showSuiviPrompt, setShowSuiviPrompt] = React.useState(false);
    const [suiviSubmitting, setSuiviSubmitting] = React.useState(false);
    const [suiviResult, setSuiviResult] = React.useState<{ ok: boolean; msg: string } | null>(null);

    const handleCreateSuivi = async () => {
        setSuiviSubmitting(true);
        try {
            const res: any = await moduleApi.createSuiviPrestationDossierFromRdv(appt.id);
            if (res?.error) { setSuiviResult({ ok: false, msg: res.error }); return; }
            setSuiviResult({ ok: true, msg: 'Dossier de suivi créé.' });
        } catch {
            setSuiviResult({ ok: false, msg: 'Erreur lors de la création du suivi.' });
        } finally {
            setSuiviSubmitting(false);
        }
    };

    const newMoneyToday = paymentStatus === 'encaisse' ? remainingAfterOnline : Math.min(remainingAfterOnline, Math.max(0, Number(newPaymentAmount || 0)));
    const alreadyPaidNum = Math.min(totalTtc, alreadyPaidOnline + newMoneyToday);
    const remainingDue = Math.max(0, totalTtc - alreadyPaidNum);
    const paymentMethodRequired = newMoneyToday > 0;
    const amountToAllocate = newMoneyToday;

    React.useEffect(() => {
        setPaymentEntries((prev) => prev.length <= 1 ? [{ method: prev[0]?.method || '', amount: amountToAllocate }] : prev);
    }, [amountToAllocate]);

    const updatePaymentEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setPaymentEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addPaymentEntry = () => {
        const allocated = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setPaymentEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((amountToAllocate - allocated) * 100) / 100) }]);
    };
    const removePaymentEntry = (idx: number) => setPaymentEntries((prev) => prev.filter((_, i) => i !== idx));
    const paymentEntriesSum = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const paymentEntriesValid = !paymentMethodRequired || (
        paymentEntries.every((e) => e.method && Number(e.amount) > 0) &&
        Math.abs(paymentEntriesSum - amountToAllocate) < 0.01
    );

    const handleValidate = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res: any = await moduleApi.completeProRdvAppointment(appt.id, {
                payment_status: paymentStatus,
                payment_entries: paymentMethodRequired ? paymentEntries : [],
                new_payment_amount: newMoneyToday,
            });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }
            setShowSuiviPrompt(true);
            setSubmitting(false);
        } catch (e: any) {
            setError('Erreur lors de la clôture');
            setSubmitting(false);
        }
    };

    const pillClass = (active: boolean) => `flex-1 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><CheckCircle size={18} className="text-emerald-400" /> Terminer le rendez-vous</h3>
                    {!showSuiviPrompt && <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>}
                </div>
                {showSuiviPrompt ? (
                    <>
                        <div className="p-5 space-y-4">
                            <p className="text-sm text-content font-semibold">Rendez-vous clôturé.</p>
                            {suiviResult ? (
                                <p className={`text-sm font-semibold ${suiviResult.ok ? 'text-emerald-400' : 'text-content-muted'}`}>{suiviResult.msg}</p>
                            ) : (
                                <p className="text-sm text-content-muted">Souhaitez-vous créer un suivi de prestation pour ce rendez-vous ?</p>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                            {suiviResult ? (
                                <button onClick={() => onDone()} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold">Fermer</button>
                            ) : (
                                <>
                                    <button onClick={() => onDone()} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Non</button>
                                    <button onClick={handleCreateSuivi} disabled={suiviSubmitting} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                                        {suiviSubmitting ? 'Création...' : 'Oui'}
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                ) : (
                <>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {totalTtc > 0 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Le rendez-vous a-t-il été payé ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setPaymentStatus('encaisse')} className={pillClass(paymentStatus === 'encaisse')}>ENCAISSER</button>
                                <button onClick={() => setPaymentStatus('a_encaisser')} className={pillClass(paymentStatus === 'a_encaisser')}>À ENCAISSER</button>
                            </div>
                            {alreadyPaidOnline > 0 && (
                                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-sm">
                                    <span className="text-emerald-400 font-semibold">Déjà réglé en ligne</span>
                                    <span className="font-bold text-emerald-400">{fmt(alreadyPaidOnline)}</span>
                                </div>
                            )}
                            {paymentStatus === 'a_encaisser' && remainingAfterOnline > 0 && (
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Montant supplémentaire encaissé aujourd'hui (optionnel)</label>
                                    <input type="number" min={0} step="0.01" value={newPaymentAmount}
                                        onChange={(e) => setNewPaymentAmount(e.target.value)}
                                        placeholder="Ex: 50"
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            )}
                            {paymentMethodRequired && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Moyen de paiement (pour le montant encaissé aujourd'hui)</p>
                                    {paymentEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface-soft border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updatePaymentEntry(idx, { method: m })}
                                                        className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${entry.method === m ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {paymentEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updatePaymentEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removePaymentEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addPaymentEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {paymentEntries.length > 1 && Math.abs(paymentEntriesSum - amountToAllocate) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({fmt(paymentEntriesSum)}) ne correspond pas au montant à régler ({fmt(amountToAllocate)}).
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="p-4 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                <div className="flex justify-between text-content-muted">
                                    <span>Total du rendez-vous</span>
                                    <span>{fmt(totalTtc)}</span>
                                </div>
                                {paymentStatus === 'a_encaisser' && alreadyPaidNum > 0 && (
                                    <div className="flex justify-between text-content-muted">
                                        <span>Déjà réglé</span>
                                        <span>- {fmt(alreadyPaidNum)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-border font-bold text-content">
                                    <span>{paymentStatus === 'encaisse' ? 'TOTAL ENCAISSÉ' : 'TOTAL NET À PAYER'}</span>
                                    <span className={paymentStatus === 'encaisse' ? 'text-emerald-400' : 'text-red-400'}>{fmt(paymentStatus === 'encaisse' ? totalTtc : remainingDue)}</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-content-muted">Ce rendez-vous n'a aucun montant associé — il sera simplement marqué comme terminé.</p>
                    )}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Annuler</button>
                    <button onClick={handleValidate} disabled={submitting || !paymentEntriesValid} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                        {submitting ? 'Validation...' : 'Valider'}
                    </button>
                </div>
                </>
                )}
            </motion.div>
        </div>
    );
}
